/**
 * RentalListing — public detail page for one published rental.
 * Route: /rental/:slug. SEO-first (no auth) + JSON-LD RealEstateListing.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, Home, Send } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function RentalListing() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  usePageTitle(listing ? `${listing.headline} — Central Rentals Canada` : "Rental — Central Rentals");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/tenant-placement/public/${slug}`);
        if (r.status === 404) { setNotFound(true); return; }
        const data = await r.json();
        setListing(data?.listing || null);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  // JSON-LD schema — Google surfaces RealEstateListing rich cards.
  useEffect(() => {
    if (!listing) return;
    const id = "rental-listing-jsonld";
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": listing.headline,
      "description": listing.description,
      "image": listing.photos?.[0],
      "brand": { "@type": "Brand", "name": "Central Rentals Canada" },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "CAD",
        "price": listing.rent_cad,
        "url": `https://centralrentals.ca/rental/${listing.slug}`,
        "availability": "https://schema.org/InStock",
      },
    });
    document.head.appendChild(s);
    return () => { document.getElementById(id)?.remove(); };
  }, [listing]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/tenant-placement/enquire/${slug}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Couldn't send your enquiry.");
      }
      toast.success("Sent! The landlord has your message and will reach out.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      toast.error(e.message || "Couldn't send your enquiry.");
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader light />
        <div className="max-w-5xl mx-auto px-5 py-12 flex items-center text-slate-500" data-testid="rental-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading rental…
        </div>
      </div>
    );
  }
  if (notFound || !listing) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader light />
        <div className="max-w-3xl mx-auto px-5 py-16 text-center" data-testid="rental-notfound">
          <Home className="w-10 h-10 mx-auto text-slate-300"/>
          <h1 className="mt-3 font-display text-2xl font-extrabold text-slate-900">This rental has been taken down</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">It may already be rented. Browse other listings in the directory.</p>
          <Link to="/find-a-place" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm">
            See all rentals <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="rental-listing-page">
      <PublicHeader light />
      <section className="max-w-5xl mx-auto px-5 pt-8 pb-12">
        <div className="text-xs">
          <Link to="/find-a-place" className="text-slate-500 hover:text-slate-900 underline underline-offset-4">← All rentals</Link>
        </div>
        <div className="mt-4 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {listing.photos?.[0] ? (
              <img src={listing.photos[0]} alt={listing.headline} className="w-full h-72 object-cover rounded-2xl" data-testid="rental-cover"/>
            ) : (
              <div className="w-full h-72 bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center rounded-2xl">
                <Home className="w-10 h-10 text-slate-400"/>
              </div>
            )}
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="rental-headline">{listing.headline}</h1>
            <div className="mt-2 text-slate-500 text-sm inline-flex items-center gap-1.5"><MapPin className="w-4 h-4"/> {listing.address_public || listing.city}</div>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-700">
              {listing.bedrooms != null && <span><b>{listing.bedrooms}</b> bed</span>}
              {listing.bathrooms != null && <span><b>{listing.bathrooms}</b> bath</span>}
              {listing.pets && <span>Pets: {listing.pets}</span>}
              {listing.available_on && <span>Available {listing.available_on}</span>}
            </div>
            <div className="mt-5 whitespace-pre-wrap text-slate-800 leading-relaxed" data-testid="rental-description">{listing.description}</div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <div className="text-3xl font-extrabold text-emerald-700 tabular-nums" data-testid="rental-price">${(listing.rent_cad || 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500 mb-4">CAD per month</div>
              <form onSubmit={submit} className="space-y-2" data-testid="rental-enquiry-form">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required data-testid="enquire-name" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" required data-testid="enquire-email" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="enquire-phone" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"/>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Say hello and mention your move-in date." required data-testid="enquire-message" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none"/>
                <button type="submit" disabled={sending} data-testid="enquire-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm">
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : <><Send className="w-4 h-4"/> Contact the landlord</>}
                </button>
                <p className="text-[11px] text-slate-500 text-center">Your message goes straight to the landlord — Central Rentals never resells your details.</p>
              </form>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
